import React, { useEffect, useState, useCallback } from "react";
import api, { API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { FieldInput, ImageField } from "@/components/CrudPage";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export function DowntimeForm({ meta }) {
  const [form, setForm] = useState({ date: today(), machine_code: "", reason: "", minutes: "", description: "", image_path: "" });
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  const load = useCallback(() => {
    api.get("/downtime-records?limit=30").then((r) => setRecords(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.date || !form.reason) { toast.error("Tarih ve duruş nedeni zorunludur"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.machine_code) {
        const m = meta.machines.find((x) => x.code === form.machine_code);
        if (m) payload.department_code = m.department_code;
      }
      for (const k of ["machine_code", "description", "image_path"]) if (!payload[k]) delete payload[k];
      await api.post("/downtime-records", payload);
      toast.success("Duruş kaydı eklendi");
      setForm({ date: today(), machine_code: "", reason: "", minutes: "", description: "", image_path: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Duruş kaydı silinsin mi?")) return;
    try { await api.delete(`/downtime-records/${id}`); toast.success("Kayıt silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="downtime-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Tarih *</Label>
          <FieldInput field={{ type: "date", name: "date" }} value={form.date} onChange={set} testid="downtime-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Makine</Label>
          <FieldInput field={{ name: "machine_code", type: "select", options: meta.machines.map((m) => ({ value: m.code, label: `${m.code} — ${m.name}` })) }}
            value={form.machine_code} onChange={set} testid="downtime-machine" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Duruş Nedeni *</Label>
          <FieldInput field={{ name: "reason", type: "select", options: (meta.downtime_reasons || []).map((r) => ({ value: r.name, label: r.name })) }}
            value={form.reason} onChange={set} testid="downtime-reason" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Süre (dk)</Label>
          <FieldInput field={{ name: "minutes", type: "number" }} value={form.minutes} onChange={set} testid="downtime-minutes" />
        </div>
        <div className="space-y-1.5 col-span-2">
          <Label className="text-xs">Açıklama</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
            data-testid="downtime-description" placeholder="Duruşun detaylı açıklaması..." className="min-h-[60px] text-sm" />
        </div>
        <div className="space-y-1.5 col-span-2">
          <Label className="text-xs">Fotoğraf</Label>
          <ImageField value={form.image_path} onChange={(v) => set("image_path", v)} testid="downtime-image" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="downtime-save">
        {saving ? "Kaydediliyor..." : "Duruş Kaydını Kaydet"}
      </Button>

      <div className="space-y-2" data-testid="downtime-records-list">
        <h4 className="text-sm font-medium text-muted-foreground">Son Duruş Kayıtları</h4>
        {records.map((r) => (
          <div key={r.id} className="flex items-start gap-3 p-3 rounded border border-border bg-secondary/30" data-testid={`downtime-row-${r.id}`}>
            {r.image_path && (
              <img src={`${API}/files/${r.image_path}`} alt="duruş" className="w-14 h-14 object-cover rounded border border-border" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{r.reason} <span className="text-muted-foreground font-mono text-xs">· {r.minutes} dk · {r.date}</span></div>
              <div className="text-xs text-muted-foreground">{r.machine_code || "-"}{r.description ? ` — ${r.description}` : ""}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" data-testid={`downtime-delete-${r.id}`} onClick={() => remove(r.id)}>
              <Trash2 size={13} />
            </Button>
          </div>
        ))}
        {records.length === 0 && <p className="text-sm text-muted-foreground">Duruş kaydı yok</p>}
      </div>
    </div>
  );
}
